import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';

// Token存储的key
const TOKEN_KEY = 'oa_access_token';

/**
 * 获取存储的Token
 */
export const getToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * 设置Token
 */
export const setToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
};

/**
 * 清除Token
 */
export const clearToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
};

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  code: number;
  data: any;
  message: string;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      // 检查响应是否符合后端约定的格式 {code, data, message}
      if (res && typeof res === 'object' && 'code' in res) {
        const { code, data, message: errorMessage } = res as unknown as ResponseStructure;
        // code 不为 0 表示业务错误
        if (code !== 0) {
          const error: any = new Error(errorMessage);
          error.name = 'BizError';
          error.info = { code, errorMessage, data };
          throw error; // 抛出自制的错误
        }
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: { code: number; errorMessage: string; data?: any } | undefined = error.info;
        if (errorInfo) {
          const { errorMessage } = errorInfo;
          // 默认显示错误消息
          message.error(errorMessage);
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        const { status } = error.response;

        // 401 未授权 - Token失效或未登录
        if (status === 401) {
          message.error('登录已过期，请重新登录');
          clearToken();
          // 跳转到登录页
          if (window.location.pathname !== '/user/login') {
            window.location.href = `/user/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`;
          }
          return;
        }

        // 403 禁止访问 - 无权限
        if (status === 403) {
          message.error('您没有权限访问该资源');
          return;
        }

        // 其他错误
        const errorData = error.response.data;
        const errorMessage = errorData?.message || errorData?.detail || `请求失败 (${status})`;
        message.error(errorMessage);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('网络异常，请稍后重试');
      } else {
        // 发送请求时出了点问题
        message.error('请求错误，请重试');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 拦截请求配置，添加Token到请求头
      const token = getToken();
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };
      }
      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 不需要在这里解包数据，useRequest 会通过 formatResult 自动提取 .data
      // 只需要确保响应格式正确，errorThrower 会处理错误情况
      return response;
    },
  ],
};
